import { useMutation } from '@apollo/client';
import React from 'react';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Toast from '../components/Toast';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import archivePostIcon from '../icons/non-prefilled/16x16/archive.svg';
import archiveDraftIcon from '../icons/non-prefilled/16x16/draftArchived.svg';
import unarchiveIcon from '../icons/non-prefilled/16x16/unarchive.svg';
import { UIActions } from '../ui/uiSlice';

const USE_TOGGLE_ARCHIVED_POST_FRAGMENT = gql(`
  fragment useToggleArchived_post on SlimPostFields {
    id
    archivedAt
    title
    publishedAt
  }
`);

const ARCHIVE_POST_MUTATION = gql(/** @complexity 4 */ `
  mutation archivePost($id: ID!, $archived: Boolean!) {
    post: updatePost(id: $id, archived: $archived) {
      id
      title
      archivedAt
      archiver {
        id
        name
      }
      ...useToggleArchived_post
    }
  }
`);

const useToggleArchived = () => {
  const [archivePost] = useMutation(ARCHIVE_POST_MUTATION);
  const dispatch = useAppDispatch();

  const toggleArchived = async (
    {
      id,
      archivedAt,
      title,
      publishedAt,
    }: DocumentType<typeof USE_TOGGLE_ARCHIVED_POST_FRAGMENT>,
    {
      skipToast,
      onComplete,
    }: { skipToast?: boolean; onComplete?: () => void } = {},
  ) => {
    const response = await archivePost({
      variables: { id, archived: !archivedAt },
    });

    onComplete?.();

    if (skipToast) {
      return null;
    }

    const archiveIcon = publishedAt ? archivePostIcon : archiveDraftIcon;

    dispatch(
      UIActions.pushToast(({ onDismiss }: { onDismiss: () => void }) => (
        <Toast
          icon={archivedAt ? unarchiveIcon : archiveIcon}
          message={
            <>
              <strong>{title}</strong> has been{' '}
              {archivedAt ? 'unarchived' : 'archived'}
            </>
          }
          secondaryText="Undo"
          onSecondaryClick={() => {
            const post = response.data?.post;
            if (post) {
              toggleArchived(post, { skipToast: true, onComplete });
            }
            onDismiss();
          }}
          onDismiss={onDismiss}
        />
      )),
    );

    return response;
  };

  return toggleArchived;
};

export default useToggleArchived;
